import ee from '../../../third_party/ee_api_js_modified';
import DynamicRasterManager from './DynamicRasterManager';
import WebGLRasterManager from './WebGLRasterManager';
import StaticRasterManager from './StaticRasterManager';
import { API_PATH } from '../../../config';

const formatTileUrl = (tileUrl) => (
    x,
    y,
    z,
) => Object.entries({ x, y, z }).reduce(
    (url, [k, v]) => url.replace(`{${k}}`, v),
    tileUrl,
);

const noop = () => undefined;
const toJSON = (result) => result.json();

/**
 * Get layer calculating elevation on fronend.
 *
 * @param {!array<Number>} altitudes Altitude step values.
 * @param {!Number} altitude Altitude value.
 * @param {!Map} map Google Map instance.
 * @param {!function} updateProgress, updateLoading progress.
 * @return {!Promise<Layer>} overlay, Elevation layer.
 */
export const getFrontendLayer = (
    altitudes,
    altitude,
    map,
    updateProgress,
) => new Promise((
    resolve,
) => fetch(`${API_PATH}/elevation/map`, { method: 'POST' })
    .then(toJSON)
    .then(({ token, mapid, urlFormat }) => {
      const tileSource = new ee.layers.EarthEngineTileSource({
        mapid,
        token,
        formatTileUrl: formatTileUrl(urlFormat),
      });

      const options = {
        altitudes,
        altitude,
        blur: 3,
        updateProgress,
      };

      let rasterManager = new WebGLRasterManager(options);

      if (rasterManager.webGLRaster.isError()) {
        rasterManager = new DynamicRasterManager(options);
      }

      const overlay = new ee.layers.RasterOverlay(
          tileSource,
          undefined,
          rasterManager,
      );

      rasterManager.setOverlay(overlay);

      resolve(overlay);
    })
    .catch(noop));

/**
 * Get layer updating elevation from backend.
 *
 * @param {!array<Number>} altitudes Altitude step values.
 * @param {!Number} altitude Altitude value.
 * @param {!Map} map Google Map instance.
 * @param {!function} updateProgress, updateLoading progress.
 * @return {!Layer} overlay, Elevation layer.
 */
export const getBackendLayer = (
    altitudes,
    altitude,
    map,
    updateProgress,
) => {
  const rasterManager = new StaticRasterManager({
    altitude,
    altitudes,
    blur: 3,
    map,
    updateProgress,
  });

  // google.maps.ImageMapType does not work on Safari.
  // It causes tiles to flicker and plain gray map.
  // ee.layers.EarthEngineTileSource does a better job.
  const tileSource = new ee.layers.EarthEngineTileSource({
    mapid: 'a',
    token: 'b',
    formatTileUrl: (x, y, z) => {
      const query = `e=${+rasterManager.altitude}`;
      const params = `${z}/${x}/${y}`;
      return `${API_PATH}/elevation/tile/${params}?${query}`;
    },
  });

  const overlay = new ee.layers.RasterOverlay(
      tileSource,
      undefined,
      rasterManager,
  );

  rasterManager.setOverlay(overlay);
  map.overlayMapTypes.setAt(0, overlay);

  return overlay;
};

export default undefined;

